import { PowerConnectionFormStep } from '@/types/forms/formSteps/powerConnectionFormStep';
import {
    isDea,
    isDischargePowerBelowOrEqualTippingPoint,
    isDischargePowerAboveTippingPoint,
    isLowVoltage,
    isPluginGeneratingPlant,
    isPowerHeatCoupling,
    isPv,
    isPvAndStorageLte30,
    isRequestWithoutPlanning,
    isStorage,
    isUnknown,
} from './common';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import { findFirstValidKey } from '@/utilities/findFirstValidKey';
import { isHigherThanOrEqual } from '@/utilities/stringParsingUtils';
import { getInvalidGenerationUnitsWithPreExisting } from '../../utilities/application/powerConnection';
import { ConnectionRulesType } from '@/types/forms/shared/connectionRulesType';

export const powerConnectionRules = async (
    formStep: {
        model: PowerConnectionFormStep;
        formModel: ApplicationForm;
    } & Vue,
): Promise<void> => {
    const hasLowVoltage = isLowVoltage(formStep.model);

    const isOther = (formModel: ApplicationForm) =>
        isDea(formModel) ||
        isPowerHeatCoupling(formModel) ||
        isUnknown(formModel);

    const requestWithoutPlanning = isRequestWithoutPlanning(formStep.formModel);

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-orderNumber
    formStep.model.orderNumber.disabled =
        formStep.model.doesConnectionExist.disabled ||
        formStep.model.doesConnectionExist.value !== 'ordered';

    formStep.model.onlyInverterCapacityOfExistingGenerationPlant.disabled =
        formStep.model.hasExistingPlants.disabled ||
        !formStep.model.hasExistingPlants.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-existingPlantsTotalPower
    formStep.model.existingPlantsTotalPower.disabled =
        formStep.model.hasExistingPlants.disabled ||
        !formStep.model.hasExistingPlants.value;

    type ExistingPlantsTotalPowerConditionalInfoKey =
        | 'pvInfo'
        | 'storageInfo'
        | 'otherInfo';

    //In-Feed Management Conditional Info
    const existingPlantsTotalPowerConditionInfo = {
        pvInfo: isPv(formStep.formModel),
        storageInfo: isStorage(formStep.formModel),
        otherInfo: isOther(formStep.formModel),
    } as Record<ExistingPlantsTotalPowerConditionalInfoKey, boolean>;

    formStep.model.existingPlantsTotalPower.conditionalInfoKey = findFirstValidKey(
        existingPlantsTotalPowerConditionInfo,
    );

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-hasExistingPlants
    formStep.model.repoweringPlanned.disabled =
        formStep.model.hasExistingPlants.disabled ||
        !formStep.model.hasExistingPlants.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-existingPlantsTotalPower
    formStep.model.repoweringTotalPower.disabled =
        formStep.model.repoweringPlanned.disabled ||
        !formStep.model.repoweringPlanned.value;

    // Units mustn't become invalid due to additional power info on this form step.
    // However, as additional power info can't allow total power to reduced,
    // we only need to check for units that have become invalid due to an increase in total power leading to 4105 certification not being enough anymore
    // DEV NOTE: The logic do identify a certificate not matching anymore is heavily reliant on knowing the requirements for all certifications,
    // as finding out the exact reason for not being valid relies on checking against all certifications, whether they are valid or invalid
    // Therefore, it's possible for this logic to somewhat easily break, should additional certifications (other than 4105 and 4110) be added
    const invalidGenerationUnits = getInvalidGenerationUnitsWithPreExisting(
        formStep.formModel.applicationType,
        formStep.formModel.plantPerformance,
        formStep.model,
    );
    formStep.model.generationUnitsFeedback.disabled = !(
        invalidGenerationUnits.length > 0 &&
        invalidGenerationUnits.some((unit) =>
            unit.norm.some((norm) => norm === ConnectionRulesType.rules4105),
        )
    );

    const isLvStorageBelow30 =
        isStorage(formStep.formModel) &&
        isLowVoltage(formStep.model) &&
        isDischargePowerBelowOrEqualTippingPoint(
            formStep.formModel.plantPerformance,
            30,
        );

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-feedInPowerShallBeLimitedByAController
    formStep.model.feedInPowerShallBeLimitedByAController.disabled =
        isPluginGeneratingPlant(formStep.formModel) ||
        (isLvStorageBelow30 && !isPvAndStorageLte30(formStep.formModel));

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-feedInPower
    formStep.model.feedInPower.disabled =
        isPluginGeneratingPlant(formStep.formModel) ||
        (isLvStorageBelow30 && !isPvAndStorageLte30(formStep.formModel));
    formStep.model.feedInPower.userChangeable =
        !formStep.model.feedInPowerShallBeLimitedByAController.disabled &&
        (formStep.model.feedInPowerShallBeLimitedByAController.value ?? false);

    const requestTypeIsWithPlanningOrDisabled =
        formStep.formModel.generationType.requestType.disabled ||
        (!formStep.formModel.generationType.requestType.disabled &&
            formStep.formModel.generationType.requestType.value ===
                'requestWithPlanning');
    const doesConnectionExistIsOrderedOrNo =
        !formStep.model.doesConnectionExist.disabled &&
        (formStep.model.doesConnectionExist.value === 'ordered' ||
            formStep.model.doesConnectionExist.value === 'no');
    const doesConnectionExistIsOrderedOrNoWithRequestTypePlanning =
        requestTypeIsWithPlanningOrDisabled && doesConnectionExistIsOrderedOrNo;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-plannedReferencePower
    formStep.model.plannedReferencePower.disabled = !(
        (isDischargePowerAboveTippingPoint(
            formStep.formModel.plantPerformance,
            30,
        ) &&
            isStorage(formStep.formModel)) ||
        (doesConnectionExistIsOrderedOrNoWithRequestTypePlanning &&
            !isStorage(formStep.formModel))
    );

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-ofWhichOwnUseGeneratingPlant
    formStep.model.ofWhichOwnUseGeneratingPlant.disabled = !(
        (isDischargePowerAboveTippingPoint(
            formStep.formModel.plantPerformance,
            30,
        ) &&
            isStorage(formStep.formModel)) ||
        (doesConnectionExistIsOrderedOrNoWithRequestTypePlanning &&
            !isStorage(formStep.formModel))
    );

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-noConnectionAvailable
    formStep.model.noConnectionAvailable.disabled =
        formStep.model.doesConnectionExist.disabled ||
        formStep.model.doesConnectionExist.value !== 'no';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-reactivePowerCompensationSystemExists
    formStep.model.reactivePowerCompensationSystemExists.disabled = hasLowVoltage;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-unitCapacity
    formStep.model.unitCapacity.disabled =
        formStep.model.reactivePowerCompensationSystemExists.disabled ||
        !formStep.model.reactivePowerCompensationSystemExists.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-unitIsPrototype
    formStep.model.unitIsPrototype.disabled = hasLowVoltage;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-buildingPowerDemand
    formStep.model.buildingPowerDemand.disabled =
        hasLowVoltage || requestWithoutPlanning;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-requiredPower
    formStep.model.requiredPower.disabled =
        formStep.model.buildingPowerDemand.disabled ||
        !formStep.model.buildingPowerDemand.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854565649/Formular+Netzanschluss#FormularNetzanschluss-fromWhen
    formStep.model.fromWhen.disabled =
        formStep.model.buildingPowerDemand.disabled ||
        !formStep.model.buildingPowerDemand.value;

    // https://netzkern.atlassian.net/browse/ED-597
    formStep.model.ownUseExceedsPlannedPowerWarning.disabled =
        formStep.model.plannedReferencePower.disabled ||
        formStep.model.ofWhichOwnUseGeneratingPlant.disabled ||
        isHigherThanOrEqual(
            formStep.model.plannedReferencePower.value,
            formStep.model.ofWhichOwnUseGeneratingPlant.value,
        );
};
